import { useEffect, useState } from 'react';
import { suppliersApi } from './api';
import { OPEN_NOTES_DOC_TYPES } from './reviewIssues';

const FULL_DATE = /^\d{2}\/\d{2}\/\d{4}$/;

// The supplier's open תעודות משלוח in the window of the invoice being entered
// ({ count, months } — server lib/unlinkedDeliveryNotes.js
// openNotesForInvoice), feeding the "הפוך לחשבונית מרכזת" chip. Asks only for
// a plain invoice type with a supplier and a complete date, so typing a date
// doesn't fire a request per keystroke. Null whenever the question does not
// apply or the fetch fails — a warning that cannot load stays silent rather
// than guessing.
export function useOpenDeliveryNotes({ enabled, supplierId, docType, date }) {
  const [data, setData] = useState(null);
  const d = String(date || '').trim();
  const applies = !!(enabled && supplierId && OPEN_NOTES_DOC_TYPES.includes(docType) && FULL_DATE.test(d));
  useEffect(() => {
    // Cleared up front, not only on failure: otherwise the previous
    // supplier's/date's answer stays on screen until this one resolves.
    setData(null);
    if (!applies) return undefined;
    let live = true;
    suppliersApi.openDeliveryNotes(supplierId, d)
      .then(r => { if (live) setData(r.data); })
      .catch(() => { if (live) setData(null); });
    return () => { live = false; };
  }, [applies, supplierId, d]);
  return applies ? data : null;
}

// The window the chip counted, as the link modal's { from, to } (YYYY-MM-DD):
// the first day of its earliest month through the invoice date — stamped with
// the supplier and date it was computed for. Null when there is nothing to
// open on.
export function openNotesRange(openDeliveryNotes, supplierId, date) {
  const first = openDeliveryNotes?.months?.[0];
  const d = String(date || '').trim();
  if (!first || !FULL_DATE.test(d)) return null;
  const [mm, yyyy] = first.split('/');
  return {
    from: `${yyyy}-${mm}-01`, to: `${d.slice(6)}-${d.slice(3, 5)}-${d.slice(0, 2)}`,
    supplierId: String(supplierId), date: d,
  };
}

// The saved range, only if it still describes the invoice being linked. A
// page outlives its document (אשר והבא advances in place; the upload page
// takes a new file) and the supplier or date can be edited after the retype —
// a stale range would open the modal on, and pre-tick, the wrong notes.
export function rangeForInvoice(saved, supplierId, date) {
  if (!saved || saved.supplierId !== String(supplierId) || saved.date !== String(date || '').trim()) return null;
  return { from: saved.from, to: saved.to };
}
